// from LoggedOutState to LoggedOutState
import { Widget, any, Updater, wait, nothing, fromJSX } from 'widgets-for-react'
import { Job } from '../../forms/job/jobState'
import { loadJobs } from '../../subscription/subscriptionApi'
import { async } from 'widgets-for-react'
import { Api, sign_up } from './api_to_state'
import { AuthState, SignUpState, PrivacyKind, isSignUpState } from './authState'
import { emptyHtml, signup_form } from './signup_form'
import { signup_success } from './signup_success'
import { isLoggedIn, options } from './utils'
import { Redirect } from '../../utils/redirect'
import { signup_logged_in } from './signup_logged_in'
import { signup_promo_code_form } from './signup_promo_code_form'

// internal states
// SignUpState
// ResendActivationState

const startLoadingLookupData = (
  currentState: SignUpState
): Widget<Updater<SignUpState>> =>
  currentState.login_data.jobs.kind == 'unloaded'
    ? fromJSX((setState: (_: Updater<SignUpState>) => void) => {
        setState((s) => ({
          ...s,
          login_data: {
            ...s.login_data,
            jobs: loadJobs(),
          },
        }))
        return emptyHtml
      })
    : nothing<Updater<SignUpState>>()

const loadLookupData = (
  currentState: SignUpState
): Widget<Updater<SignUpState>> =>
  currentState.login_data.jobs.kind == 'loading'
    ? async<Job[]>(currentState.login_data.jobs).map((updater) => (s) => {
        let newData = updater(s.login_data.jobs)
        return {
          ...s,
          login_data: {
            ...s.login_data,
            jobs: newData,
          },
        }
      })
    : nothing<Updater<SignUpState>>()

const lookupDataLoader = (currentState: AuthState): Widget<Updater<AuthState>> =>
  currentState.status == 'logged out' &&
  isSignUpState(currentState) &&
  currentState.signUpState == 'sign up'
    ? any<Updater<AuthState>>([
        startLoadingLookupData(currentState).map(
          (updater) => (s) => updater(s as SignUpState)
        ),
        loadLookupData(currentState).map(
          (updater) => (s) => updater(s as SignUpState)
        ),
      ])
    : nothing<Updater<AuthState>>()

export function signup(
  options: options,
  api: Api,
  privacyKind: PrivacyKind,
  authState: AuthState,
  onTermsAndConditions: JSX.Element,
  originPath: string,
  type: 'regular' | 'student' | 'promo',
  suppressHotjar: boolean
): Widget<Updater<AuthState>> {
  return isSignUpState(authState)
    ? isLoggedIn(authState)
      ? signup_logged_in(options, originPath, type)
      : any<Updater<AuthState>>([
          authState.signUpState == 'sign up' ||
          authState.signUpState == 'sign up - error form validation' ||
          authState.signUpState == 'sign up - error invalid email' ||
          authState.signUpState == 'sign up - error invalid username' ||
          authState.signUpState == 'sign up - error account already exists' ||
          authState.signUpState == 'sign up - error account not active' ||
          authState.signUpState == 'sign up - error too many attempts' ||
          authState.signUpState == 'sign up - error weak password'
            ? any<Updater<AuthState>>([
                lookupDataLoader(authState),
                signup_form(
                  options,
                  api,
                  false,
                  authState,
                  onTermsAndConditions,
                  authState.signUpState ==
                    'sign up - error too many attempts' ||
                    authState.signUpState ==
                      'sign up - error account not active',
                  type,
                  suppressHotjar
                ),
              ])
            : authState.signUpState == 'sign up - api error'
            ? any<Updater<AuthState>>([
                lookupDataLoader(authState),
                wait(500, {
                  key: 'login-widget-sign-up-promise',
                })(
                  () => (s : AuthState) : AuthState =>
                    (s as SignUpState).signUpState != 'sign up - api error'
                      ? s
                      : { ...s, signUpState: 'sign up - signing up' }
                ),
                signup_form(
                  options,
                  api,
                  false,
                  authState,
                  onTermsAndConditions,
                  true,
                  type,
                  suppressHotjar
                ),
              ])
            : authState.signUpState == 'sign up - promo code' ||
              authState.signUpState == 'sign up - error invalid promo code'
            ? signup_promo_code_form(options, authState, false)
            : authState.signUpState == 'sign up - signing up'
            ? any<Updater<AuthState>>([
                lookupDataLoader(authState),
                sign_up(
                  api,
                  {
                    ...authState.login_data,
                    isStudent: type === 'student' || false,
                  },
                  privacyKind,
                  originPath
                ),
                type === 'promo'
                  ? signup_promo_code_form(options, authState, true)
                  : signup_form(
                      options,
                      api,
                      true,
                      authState,
                      onTermsAndConditions,
                      false,
                      type,
                      suppressHotjar
                    ),
              ])
            : authState.signUpState == 'sign up - success'
            ? signup_success(options, type)
            : // as there is no transition from here back to sign up, the user must refresh the page to re-submit the form
              // possible fixes:
              // 1. present a button here 'register another account' that resets the signin state
              // 2. redirect upon success, and reset signin state
              nothing(),
        ])
    : nothing()
}
